"use client";

import React from "react";
import { Button, Input, Link } from "@heroui/react";
import { Icon } from "@iconify/react";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router";
import RightImage from '../sign-in/imagepartner.jpg'
import { AcmeIcon } from "../../client/sign-in/acme";
import { parseApiErrorWithFields } from "../../utils/errorParser";
import './index.css'
import { Typography, CustomCheckbox, AlertBlock } from "../../components";
import { buttonColors } from "../../theme/colors";
import { LogoPartnerIc } from "../../components/icons";
import { useTranslations } from "../../contexts/translation.context";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";

// Password strength calculation
const calculatePasswordStrength = (password: string) => {
  let strength = 0;
  if (/[a-z]/.test(password)) strength++; // lowercase letter
  if (/[A-Z]/.test(password)) strength++; // uppercase letter
  if (/[0-9]/.test(password)) strength++; // digit
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength++; // symbol
  return strength;
};

// Get password strength color based on overall complexity
const getPasswordStrengthColor = (strengthLevel: number) => {
  if (strengthLevel >= 4) return '#19be60'; // Green - strong (all 4 criteria)
  if (strengthLevel >= 3) return '#006fee'; // Blue - good (3 criteria)
  if (strengthLevel >= 2) return '#e8a323'; // Orange - medium (2 criteria)
  if (strengthLevel >= 1) return '#f31260'; // Red - weak (1 criteria)
  return '#d4d4d8'; // Gray - none
};

// Partner sign up schema
const createPartnerSignUpSchema = (
  t: (key: string) => string,
  inviteToken?: string | null,
  inviteEmail?: string | null
) => {
  const baseSchema: any = {
    firstName: Yup.string()
      .min(2, t('auth.validation.min2Chars'))
      .required(t('auth.validation.required')),
    telegram: Yup.string()
      .min(2, t('auth.validation.telegramMin'))
      .required(t('auth.validation.telegramRequired')),
    password: Yup.string()
      .min(8, t('auth.validation.passwordRequirements'))
      .max(25, t('auth.validation.passwordRequirements'))
      .matches(/[a-z]/, t('auth.validation.passwordRequirements')) // lowercase letter
      .matches(/[A-Z]/, t('auth.validation.passwordRequirements')) // uppercase letter
      .matches(/[0-9]/, t('auth.validation.passwordRequirements')) // digit
      .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, t('auth.validation.passwordRequirements')) // symbol
      .required(t('auth.validation.passwordRequired')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], t('auth.validation.passwordsMatch'))
      .required(t('auth.validation.confirmPasswordRequired')),
    acceptTerms: Yup.boolean(), // Убираем валидацию, оставляем только тип
  };

  // Добавляем email только если нет токена или есть email
  if (!(inviteToken && !inviteEmail)) {
    baseSchema.email = Yup.string()
      .email(t('auth.validation.emailInvalid'))
      .required(t('auth.validation.emailRequired'));
  }

  return Yup.object().shape(baseSchema);
};

export default function PartnerSignUp() {
  const { t, lang } = useTranslations();
  const [currentStep, setCurrentStep] = React.useState<'select' | 'form'>('select');
  const [isVisible, setIsVisible] = React.useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  // --- Новый код для поддержки регистрации по токену ---
  const [inviteToken, setInviteToken] = React.useState<string | null>(null);
  const [inviteName, setInviteName] = React.useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = React.useState<string | null>(null);

  const { signup, googleAuthPartner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const name = params.get('name');
    const email = params.get('email');
    if (token) setInviteToken(token);
    if (name) setInviteName(name);
    if (email) setInviteEmail(email);
    // Если есть токен, сразу переходим к форме регистрации
    if (token) {
      setCurrentStep('form');
    }
  }, [location.search]);

  React.useEffect(() => {
    if (location.state?.skipSelect && location.state?.userType === 'marketer') {
      setCurrentStep('form');
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const toggleVisibility = () => setIsVisible(!isVisible);
  const toggleConfirmVisibility = () => setIsConfirmVisible(!isConfirmVisible);

  const handleMarketerSelect = () => {
    setCurrentStep('form');
  };

  const handleBackToSelect = () => {
    setCurrentStep('select');
    setError(null);
  };

  const getInitialValues = () => {
    return {
      firstName: inviteName || '',
      email: inviteEmail || '',
      telegram: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    };
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    if (!values.acceptTerms) {
      setSubmitAttempted(true);
      setSubmitting(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setSubmitAttempted(false);

      if (inviteToken) {
        // Регистрация через googleAuthRegister с токеном
        const { default: partnerAuthService } =
          await import('../../api/partner/partnerAuthService');
        const { setClientToken } = await import('../../api/apiClient');
        const response = await partnerAuthService.googleAuthRegister(
          {
            ...values,
            type: 'partner',
            name: values.firstName,
          },
          inviteToken
        );
        // Если есть access_token и refresh_token, авторизуем юзера сразу
        if (response?.data?.access_token) {
          const accessToken = `Bearer ${response.data.access_token}`;
          localStorage.setItem('authToken', accessToken);
          if (response.data.refresh_token) {
            localStorage.setItem('refreshToken', response.data.refresh_token);
          }
          localStorage.setItem('userRole', 'partner');
          setClientToken(accessToken);
          // Получаем профиль и авторизуем
          const me = await partnerAuthService.getMe({});
          // setUser из AuthContext
          if (typeof window !== 'undefined') {
            const { useAuth } = await import('../../contexts/AuthContext');
            try {
              // useAuth() нельзя вызвать вне компонента, поэтому делаем window.location
              window.location.replace('/partner/dashboard');
              return;
            } catch {}
          }
        }
      } else {
        await signup(
          {
            ...values,
            type: 'partner',
            name: values.firstName,
          },
          'partner'
        );
      }

      // Redirect to sign-in
      navigate('/partner/sign-in', {
        state: {
          skipSelect: true,
          message: t('auth.success.registrationSuccess'),
          from: location.state?.from,
        },
      });
    } catch (error: any) {
      console.error('Registration failed:', error);
      setError(parseApiErrorWithFields(error, t('auth.errors.registrationError')));
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  // User Type Selection Step
  if (currentStep === 'select') {
    return (
      <div className="relative flex min-h-screen w-full items-center justify-center bg-white px-4 md:px-6 pt-[5.25rem] pb-4 md:py-0">
        <div className="flex flex-col justify-end md:justify-normal fixed top-0 left-0 w-full z-50 bg-white md:bg-transparent border-b border-solid border-neutral-12 px-4 py-6 md:px-12 md:pt-12 md:border-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center justify-center w-[4.75rem] h-[1.375rem] md:w-[7.5rem] md:h-9 shrink-0">
              <LogoPartnerIc className="size-full" />
            </div>
            <div className="md:hidden">
              <LanguageSwitcher variant="ghost" />
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-center md:py-0 py-6">
          <div className="flex w-full max-w-[26.5rem] flex-col items-center gap-6 md:gap-4">
            <div className="w-full text-center flex flex-col gap-1 md:gap-2">
              <Typography variant="h3">{t('auth.signUp')}</Typography>
              <Typography className="text-auth-muted" variant="body1">
                {t('auth.selectAccountType')}
              </Typography>
            </div>

            <div className="flex w-full flex-col gap-2 md:mt-4">
              {/* Creator / Agency Card */}
              <div
                onClick={() =>
                  navigate('/client/sign-up', { state: { userType: 'creator', skipSelect: true } })
                }
                className="w-full cursor-pointer bg-auth-surface rounded-2 p-4 py-5 hover:bg-auth-surface-hover transition-all user-type-card duration-300"
              >
                <div className="flex items-start gap-2">
                  <div className="flex items-center justify-center size-6 md:size-9 shrink-0">
                    <Icon
                      width={36}
                      height={36}
                      icon="solar:user-rounded-linear"
                      className="text-auth-ink size-full"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography variant="h7">
                      {t('sidebar.creator')} / {t('sidebar.agency')}
                    </Typography>
                    <Typography variant="body2" className="text-auth-muted">
                      {t('auth.descriptions.creator')}
                    </Typography>
                  </div>
                </div>
              </div>

              {/* Marketer Card */}
              <div
                onClick={handleMarketerSelect}
                className="w-full cursor-pointer bg-auth-surface rounded-2 p-4 py-5 hover:bg-auth-surface-hover transition-all user-type-card duration-300"
              >
                <div className="flex items-start gap-2">
                  <div className="flex items-center justify-center size-6 md:size-9 shrink-0">
                    <Icon
                      width={36}
                      height={36}
                      icon="solar:user-id-outline"
                      className="text-auth-ink size-full"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography variant="h7">{t('sidebar.marketer')}</Typography>
                    <Typography variant="body2" className="text-auth-muted">
                      {t('auth.descriptions.marketer')}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            {/* Sign-in Link */}
            <div className="w-full flex flex-wrap gap-4 justify-between md:mt-4">
              <Typography variant="body2" className="text-auth-muted">
                {t('auth.haveAccount')}&nbsp;
              </Typography>
              <Link
                href="/partner/sign-in"
                size="sm"
                className="text-sm leading-[1.125rem]"
                style={{ color: buttonColors.partner.primary.background }}
              >
                {t('auth.signIn')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Registration Form Step
  return (
    <div className="relative flex min-h-screen w-full items-center justify-center bg-white px-4 md:px-6 pt-[5.25rem] pb-4 md:py-0">
      <div className="flex flex-col justify-end md:justify-normal fixed top-0 left-0 w-full z-50 bg-white md:bg-transparent border-b border-solid border-neutral-12 px-4 py-6 md:px-12 md:pt-12 md:border-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center justify-center w-[4.75rem] h-[1.375rem] md:w-[7.5rem] md:h-9 shrink-0">
            <LogoPartnerIc className="size-full" />
          </div>
          <div className="md:hidden">
            <LanguageSwitcher variant="ghost" />
          </div>
        </div>
      </div>

      <div className="flex w-full md:min-h-[calc(100vh-48px)] flex-col justify-center md:flex-row md:items-stretch">
        <div className="flex w-full items-center justify-center bg-background py-6 md:px-6 md:py-12 md:flex-1">
          <div className="flex w-full max-w-[26.5rem] flex-col items-center">
            {/* Title */}
            <div className="w-full text-center mb-6 md:mb-8">
              <Typography variant="h3">{t('auth.signUp')}</Typography>
            </div>
            <div className="w-full mb-4">
              <AlertBlock
                data={[{
                  severity: "info",
                  title: t('auth.emailVerification') || '',
                  description: t('auth.emailVerificationRequired')
                }]}
              />
            </div>
            {error && (
              // <div className="w-full p-3 text-sm text-red-500 bg-red-50 border border-red-200 rounded mb-4 whitespace-pre-line">
              //   {error}
              // </div>
              <div className="w-full mb-4 bg-auth-error-bg border-2 border-auth-error-border rounded-3 p-4 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="size-10 bg-auth-error-bg border border-auth-error-border rounded-[1.875rem] flex items-center justify-center flex-shrink-0">
                    <Icon
                      icon="mingcute:information-fill"
                      width="19"
                      height="19"
                      className="text-auth-error-text"
                    />
                  </div>
                  <p className="text-auth-error-text text-sm font-medium leading-5">{error}</p>
                </div>
              </div>
            )}

            <Formik
              key={String(!!inviteToken) + String(!!inviteEmail)} // Re-init form only if token/email меняются
              initialValues={getInitialValues()}
              validationSchema={createPartnerSignUpSchema(t, inviteToken, inviteEmail)}
              onSubmit={handleSubmit}
              onInvalidSubmit={(values: any) => {
                if (!values.acceptTerms) {
                  setSubmitAttempted(true);
                }
              }}
            >
              {({ handleSubmit, isSubmitting, values, setFieldValue, errors, isValid }) => (
                <form
                  className="flex w-full flex-col gap-4"
                  onSubmit={(e) => {
                    handleSubmit(e);
                  }}
                  noValidate
                >
                  {/* Name Field */}
                  <div className="flex flex-col w-full">
                    <Input
                      isRequired
                      label={t('auth.name')}
                      name="firstName"
                      placeholder={t('placeholders.enterName')}
                      variant="bordered"
                      value={values.firstName}
                      onChange={(e) => setFieldValue('firstName', e.target.value)}
                      labelPlacement="inside"
                      className="w-full custom-required-label-partner"
                      classNames={{
                        inputWrapper: 'h-14',
                        input: 'text-black',
                      }}
                    />
                    <ErrorMessage
                      name="firstName"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Email Field (conditionally render) */}
                  {!(inviteToken && !inviteEmail) && (
                    <div className="flex flex-col w-full">
                      <Input
                        isRequired
                        label={t('labels.email')}
                        name="email"
                        placeholder={t('placeholders.enterEmail')}
                        type="email"
                        variant="bordered"
                        value={values.email}
                        onChange={(e) => setFieldValue('email', e.target.value)}
                        labelPlacement="inside"
                        className="w-full custom-required-label-partner"
                        classNames={{
                          inputWrapper: 'h-14',
                          input: 'text-black',
                        }}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  )}

                  {/* Telegram Field */}
                  <div className="flex flex-col w-full">
                    <Input
                      isRequired
                      label={t('labels.telegram')}
                      name="telegram"
                      placeholder={t('placeholders.enterTelegram')}
                      variant="bordered"
                      value={values.telegram}
                      onChange={(e) => setFieldValue('telegram', e.target.value)}
                      labelPlacement="inside"
                      className="w-full custom-required-label-partner"
                      classNames={{
                        inputWrapper: 'h-14',
                        input: 'text-black',
                      }}
                    />
                    <ErrorMessage
                      name="telegram"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Password Field */}
                  <div className="flex flex-col w-full">
                    <Input
                      isRequired
                      endContent={
                        <button type="button" onClick={toggleVisibility}>
                          {isVisible ? (
                            <Icon
                              className="pointer-events-none text-2xl text-default-400"
                              icon="solar:eye-closed-linear"
                            />
                          ) : (
                            <Icon
                              className="pointer-events-none text-2xl text-default-400"
                              icon="solar:eye-bold"
                            />
                          )}
                        </button>
                      }
                      label={t('labels.password')}
                      name="password"
                      placeholder={t('placeholders.enterPassword')}
                      type={isVisible ? 'text' : 'password'}
                      variant="bordered"
                      value={values.password}
                      onChange={(e) => setFieldValue('password', e.target.value)}
                      labelPlacement="inside"
                      className="w-full custom-required-label-partner"
                      classNames={{
                        inputWrapper: 'h-14',
                        input: 'text-black',
                      }}
                    />

                    {/* Password Strength Indicator */}
                    <div className="flex gap-1 w-full mt-2">
                      {[1, 2, 3, 4].map((level) => {
                        const currentStrength = calculatePasswordStrength(values.password);
                        const isActive = currentStrength >= level;
                        const strengthColor = getPasswordStrengthColor(currentStrength);
                        const color = isActive ? strengthColor : '#d4d4d8';

                        return (
                          <div
                            key={level}
                            className="h-1.5 flex-1 rounded-3.5 password-strength-bar"
                            style={{ backgroundColor: color }}
                          />
                        );
                      })}
                    </div>

                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                    <p className="text-sm text-auth-muted leading-5 mt-2">
                      {t('auth.passwordHint')}
                    </p>
                  </div>

                  {/* Confirm Password Field */}
                  <div className="flex flex-col w-full">
                    <Input
                      isRequired
                      endContent={
                        <button type="button" onClick={toggleConfirmVisibility}>
                          {isConfirmVisible ? (
                            <Icon
                              className="pointer-events-none text-2xl text-default-400"
                              icon="solar:eye-closed-linear"
                            />
                          ) : (
                            <Icon
                              className="pointer-events-none text-2xl text-default-400"
                              icon="solar:eye-bold"
                            />
                          )}
                        </button>
                      }
                      label={t('labels.confirmPassword')}
                      name="confirmPassword"
                      placeholder={t('placeholders.confirmPassword')}
                      type={isConfirmVisible ? 'text' : 'password'}
                      variant="bordered"
                      value={values.confirmPassword}
                      onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
                      labelPlacement="inside"
                      className="w-full custom-required-label-partner"
                      classNames={{
                        inputWrapper: 'h-14',
                        input: 'text-black',
                      }}
                    />
                    <ErrorMessage
                      name="confirmPassword"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Terms Acceptance */}
                  <div className="flex w-full items-center flex-col gap-2 mb-4">
                    <div className="flex w-full items-center flex-wrap gap-1">
                      <CustomCheckbox
                        name="acceptTerms"
                        size="sm"
                        isSelected={values.acceptTerms}
                        className="whitespace-nowrap text-sm"
                        onValueChange={(checked: boolean) => {
                          setFieldValue('acceptTerms', checked);
                          setSubmitAttempted(true);

                          if (checked) {
                            setSubmitAttempted(false);
                          }
                        }}
                      >
                        {t('auth.ageAgreement')}
                      </CustomCheckbox>
                      <Link
                        href={`https://onlyu.market/${lang || 'en'}/terms-of-service`}
                        isExternal
                        className="whitespace-nowrap text-sm"
                        style={{ color: buttonColors.partner.primary.background }}
                      >
                        {t('auth.termsOfUse')}
                      </Link>
                    </div>
                  </div>
                  {/* Warning Alert for unchecked terms */}
                  {!values.acceptTerms && submitAttempted && (
                    <div className="w-full mb-4 bg-auth-error-bg border-2 border-auth-error-border rounded-3 p-4 shadow-sm">
                      <div className="flex items-start gap-4">
                        <div className="size-10 bg-auth-error-bg border border-auth-error-border rounded-[1.875rem] flex items-center justify-center flex-shrink-0">
                          <Icon
                            icon="mingcute:information-fill"
                            width="19"
                            height="19"
                            className="text-auth-error-text"
                          />
                        </div>
                        <p className="text-auth-error-text text-sm font-medium leading-5">
                          {t('auth.validation.termsRequired')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    className="w-full h-12 text-base mb-2 md:mb-4"
                    type="submit"
                    isLoading={isLoading || isSubmitting}
                    disabled={isLoading || isSubmitting}
                    onClick={() => {
                      setSubmitAttempted(true);
                    }}
                    style={{
                      backgroundColor: buttonColors.partner.primary.background,
                      color: buttonColors.partner.primary.text,
                    }}
                  >
                    {isLoading || isSubmitting ? t('auth.registering') : t('auth.register')}
                  </Button>
                </form>
              )}
            </Formik>

            {/* User Type Toggle */}
            <div className="flex w-full flex-col gap-2 mb-6">
              <div
                onClick={() =>
                  navigate('/client/sign-up', { state: { userType: 'creator', skipSelect: true } })
                }
                className="w-full cursor-pointer h-12 bg-auth-surface rounded-2 p-3 hover:bg-auth-surface-hover transition-colors user-type-card flex items-center justify-center"
              >
                <div className="flex items-center gap-2">
                  <p className="text-base font-medium text-auth-ink">
                    {t('auth.registrationForCreator')}
                  </p>
                </div>
              </div>
            </div>

            {/* Sign-in Link */}
            <div className="w-full flex justify-between">
              <p className="text-sm leading-[1.125rem]">{t('auth.haveAccount')}&nbsp;</p>
              <Link
                href="/partner/sign-in"
                size="sm"
                className="text-sm leading-[1.125rem]"
                style={{ color: buttonColors.partner.primary.background }}
              >
                {t('auth.signIn')}
              </Link>
            </div>
          </div>
        </div>

        <div className="hidden w-full items-stretch gap-12 md:flex md:flex-1">
          <div className="shrink-0">
            <LanguageSwitcher variant="ghost" />
          </div>
          <div
            className="hidden h-full flex-1 overflow-hidden rounded-8 md:block"
            style={{
              backgroundImage: `url(${RightImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>
      </div>
    </div>
  );
}
